import React, { useState, useEffect, useRef } from 'react';
import { MonacoEditor } from './MonacoEditor';
import { getDefaultTheme } from '../../../themes/themes';
import './FileViewerModal.css';

/**
 * Icon - A stable wrapper for Feather icons to prevent React from crashing
 * when Feather replaces the <i> tag with an <svg> tag.
 */
const Icon = ({ name, className = '' }) => {
  const iconRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.feather && iconRef.current) {
      window.feather.replace();
    }
  }, [name]);

  return (
    <span className={`icon-wrapper ${className}`} ref={iconRef}>
      <i data-feather={name}></i>
    </span>
  );
};

/**
 * Check if a file is text-based by MIME type or extension
 */
const isTextFile = (fileType, fileName) => {
  if (!fileType && !fileName) return false;

  // Check MIME type
  if (fileType) {
    if (fileType.startsWith('text/')) return true;
    if (fileType === 'application/json') return true;
    if (fileType === 'application/javascript') return true;
    if (fileType === 'application/xml') return true;
    if (fileType === 'application/x-sh') return true;
    if (fileType === 'application/x-python') return true;
  }

  // Check file extension
  if (fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const textExtensions = [
      'txt', 'md', 'json', 'js', 'jsx', 'ts', 'tsx', 'html', 'css', 'scss', 'sass',
      'xml', 'yaml', 'yml', 'ini', 'conf', 'config', 'log', 'sh', 'bash', 'zsh',
      'py', 'java', 'c', 'cpp', 'h', 'hpp', 'cs', 'php', 'rb', 'go', 'rs', 'swift',
      'kt', 'dart', 'sql', 'r', 'm', 'pl', 'lua', 'vim', 'gitignore', 'dockerfile',
      'env', 'properties', 'toml', 'lock', 'gradle', 'maven', 'pom', 'xml'
    ];
    if (textExtensions.includes(ext)) return true;
  }

  return false;
};

/**
 * Decode base64 data URL to text content
 */
const decodeBase64DataURL = (dataURL) => {
  try {
    // data:image/png;base64,iVBORw0KG... -> extract base64 part
    const base64Match = dataURL.match(/^data:[^;]+;base64,(.+)$/);
    if (!base64Match) {
      // Try without base64 prefix
      const directMatch = dataURL.match(/^data:[^,]+,(.+)$/);
      if (directMatch) {
        return atob(directMatch[1]);
      }
      return null;
    }
    return atob(base64Match[1]);
  } catch (error) {
    console.error('Failed to decode base64 data URL:', error);
    return null;
  }
};

/**
 * Get language for Monaco editor based on file extension or MIME type
 */
const getLanguageFromFile = (fileName, fileType) => {
  if (!fileName && !fileType) return 'plaintext';

  if (fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const languageMap = {
      'js': 'javascript', 'jsx': 'javascript', 'mjs': 'javascript',
      'ts': 'typescript', 'tsx': 'typescript',
      'py': 'python',
      'html': 'html', 'htm': 'html',
      'css': 'css', 'scss': 'scss', 'sass': 'sass',
      'json': 'json',
      'xml': 'xml',
      'md': 'markdown',
      'sh': 'shell', 'bash': 'shell', 'zsh': 'shell',
      'sql': 'sql',
      'yaml': 'yaml', 'yml': 'yaml',
      'java': 'java',
      'c': 'c', 'cpp': 'cpp', 'h': 'c', 'hpp': 'cpp',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust',
      'swift': 'swift',
      'kt': 'kotlin',
      'dart': 'dart',
      'r': 'r',
      'lua': 'lua',
      'vim': 'vim',
      'dockerfile': 'dockerfile',
      'env': 'plaintext',
      'properties': 'properties',
      'toml': 'toml',
      'log': 'plaintext'
    };
    if (languageMap[ext]) return languageMap[ext];
  }

  if (fileType) {
    if (fileType === 'application/json') return 'json';
    if (fileType === 'application/javascript') return 'javascript';
    if (fileType.startsWith('text/')) {
      if (fileType.includes('html')) return 'html';
      if (fileType.includes('css')) return 'css';
      if (fileType.includes('javascript')) return 'javascript';
      return 'plaintext';
    }
  }

  return 'plaintext';
};

/**
 * FileViewerModal Component
 * Displays files in a gallery view with navigation arrows and thumbnail strip
 * Shows text files in Monaco editor (read-only), non-text files show indicator
 */
export function FileViewerModal({ isOpen, onClose, fileSegments, initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [fileContents, setFileContents] = useState({});
  const [loadingContent, setLoadingContent] = useState(false);
  const thumbnailRef = useRef(null);

  // Update current index when initialIndex changes (e.g., when opening modal)
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  // Load file content when current file changes
  useEffect(() => {
    if (!isOpen || !fileSegments || fileSegments.length === 0) return;

    const currentSegment = fileSegments[currentIndex];
    if (!currentSegment) return;

    // Check if we already have the content cached
    if (fileContents[currentIndex]) return;

    // Check if it's a text file
    const fileName = currentSegment.formats?.fileName || '';
    const fileType = currentSegment.formats?.fileType || '';
    
    if (!isTextFile(fileType, fileName)) {
      // Not a text file, no need to load content
      return;
    }

    // Load text content
    setLoadingContent(true);
    try {
      const decoded = decodeBase64DataURL(currentSegment.content);
      if (decoded !== null) {
        setFileContents(prev => ({
          ...prev,
          [currentIndex]: decoded
        }));
      }
    } catch (error) {
      console.error('Failed to decode file content:', error);
    } finally {
      setLoadingContent(false);
    }
  }, [isOpen, currentIndex, fileSegments, fileContents]);

  // Re-initialize Feather icons when component updates
  useEffect(() => {
    if (typeof window !== 'undefined' && window.feather && isOpen) {
      const timer = setTimeout(() => {
        try {
          window.feather.replace();
        } catch (e) {
          // Ignore errors during replacement
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentIndex]);

  // Scroll thumbnail into view when current index changes
  useEffect(() => {
    if (thumbnailRef.current && isOpen) {
      const thumbnail = thumbnailRef.current.querySelector(`[data-index="${currentIndex}"]`);
      if (thumbnail) {
        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentIndex, isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen || !fileSegments || fileSegments.length === 0) return;

    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < fileSegments.length - 1;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && hasPrevious) {
        setCurrentIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight' && hasNext) {
        setCurrentIndex(prev => Math.min(fileSegments.length - 1, prev + 1));
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, fileSegments, onClose]);

  if (!isOpen || !fileSegments || fileSegments.length === 0) {
    return null;
  }

  const currentSegment = fileSegments[currentIndex];
  const fileName = currentSegment.formats?.fileName || 'file';
  const fileType = currentSegment.formats?.fileType || '';
  const fileSize = currentSegment.formats?.fileSize || 0;
  const isText = isTextFile(fileType, fileName);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < fileSegments.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="file-viewer-modal-overlay" onClick={handleBackdropClick}>
      <div className="file-viewer-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          className="file-viewer-modal-close"
          onClick={onClose}
          aria-label="Close"
          title="Close (Esc)"
        >
          <Icon name="x" />
        </button>

        {/* Main file container */}
        <div className="file-viewer-main">
          {/* Previous arrow */}
          {hasPrevious && (
            <button
              className="file-viewer-nav file-viewer-nav-left"
              onClick={handlePrevious}
              aria-label="Previous file"
              title="Previous (←)"
            >
              <Icon name="chevron-left" />
            </button>
          )}

          {/* File display */}
          <div className="file-viewer-content">
            {isText ? (
              <div className="file-viewer-text-container">
                {loadingContent ? (
                  <div className="file-viewer-loading">Loading file content...</div>
                ) : (
                  <MonacoEditor
                    value={fileContents[currentIndex] || ''}
                    onChange={() => {}} // No-op for read-only
                    language={getLanguageFromFile(fileName, fileType)}
                    readOnly={true}
                    theme={getDefaultTheme()}
                  />
                )}
              </div>
            ) : (
              <div className="file-viewer-non-text">
                <div className="file-viewer-non-text-icon">
                  <Icon name="file" />
                </div>
                <div className="file-viewer-non-text-message">
                  This file cannot be displayed as text
                </div>
                <div className="file-viewer-non-text-info">
                  <div className="file-viewer-file-name">{fileName}</div>
                  <div className="file-viewer-file-meta">
                    {fileType && <span>{fileType}</span>}
                    {fileSize > 0 && <span>{formatFileSize(fileSize)}</span>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Next arrow */}
          {hasNext && (
            <button
              className="file-viewer-nav file-viewer-nav-right"
              onClick={handleNext}
              aria-label="Next file"
              title="Next (→)"
            >
              <Icon name="chevron-right" />
            </button>
          )}
        </div>

        {/* Controls bar */}
        <div className="file-viewer-controls">
          {/* File counter and name */}
          <div className="file-viewer-file-info">
            <div className="file-viewer-counter">
              {currentIndex + 1} / {fileSegments.length}
            </div>
            <div className="file-viewer-file-name-display">{fileName}</div>
          </div>
        </div>

        {/* Thumbnail strip */}
        <div className="file-viewer-thumbnails" ref={thumbnailRef}>
          {fileSegments.map((segment, index) => {
            const segFileName = segment.formats?.fileName || 'file';
            const segFileType = segment.formats?.fileType || '';
            const isTextFileType = isTextFile(segFileType, segFileName);
            
            return (
              <div
                key={index}
                data-index={index}
                className={`file-viewer-thumbnail ${index === currentIndex ? 'active' : ''}`}
                onClick={() => handleThumbnailClick(index)}
                title={segFileName}
              >
                <div className="file-viewer-thumbnail-icon">
                  <Icon name={isTextFileType ? 'file-text' : 'file'} />
                </div>
                <div className="file-viewer-thumbnail-name">
                  {segFileName.length > 15 ? `${segFileName.substring(0, 12)}...` : segFileName}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

